import Link from 'next/link'
import cx from 'classnames'
import styles from './IconNavigationGroup.module.scss'
import { Fragment } from 'react'
import { DynamicIcon } from '~elements'
import { useRouter } from 'next/router'
import { IconNavigationGroupProps } from '~types'
import { normalizePath } from 'utils'
import { fireAnalyticsEvent, getGtmLinkPosition } from '~analyticsUtils'

const IconNavigationGroup = ({
  icons,
  alignment = 'center',
  isLoading,
  parentLinkModule = '',
  removePadding,
  theme
}: IconNavigationGroupProps) => {
  const { asPath } = useRouter()
  const { activeBackgroundColor = 'lightest', hoverBackgroundColor = 'primary-dark' } = theme || {}
  const alignmentClasses = {
    left: styles['align-left'],
    center: styles['align-center'],
    right: styles['align-right']
  }
  const linkModule = `${parentLinkModule && `${parentLinkModule}_`}navigation_icon`

  const handleAnalyticsEvent = (title: string, url: string | null, index: number) => {
    fireAnalyticsEvent(url, asPath, {
      clickTracking: `${linkModule}:${title}`,
      linkModule: linkModule,
      linkLocation: getGtmLinkPosition(icons, index)
    })
  }

  return (
    <div
      className={cx(
        styles['icon-navigation-group'],
        alignmentClasses[alignment],
        removePadding && styles['padding-none'],
        isLoading && styles['disabled']
      )}
    >
      {icons.map((icon, index: number) => {
        const { iconName, isDisabled = false, onClick, title, url = null } = icon || {}
        if (!iconName || (!url && !onClick)) return null

        const isActiveView = url && normalizePath(asPath) === normalizePath(url)

        const sharedProps = {
          className: cx([
            styles['icon-navigation-group__item'],
            isActiveView && styles['is-active'],
            styles[`active-bg-color-${activeBackgroundColor}`],
            styles[`hover-bg-color-${hoverBackgroundColor}`],
            isLoading && styles[`is-loading`]
          ]),
          title,
          onClick: () => {
            onClick && onClick(), !isDisabled && !isActiveView && handleAnalyticsEvent(title, url, index)
          }
        }

        return (
          <Fragment key={`view-button-${index}`}>
            {url && !isDisabled && !isLoading ? (
              <Link href={url} {...sharedProps}>
                <DynamicIcon name={iconName} />
              </Link>
            ) : (
              <button {...sharedProps} aria-label={title} disabled={isDisabled || isLoading}>
                <DynamicIcon name={iconName} />
              </button>
            )}
          </Fragment>
        )
      })}
    </div>
  )
}

export default IconNavigationGroup
